/**
 * Info Cards Stack Slider
 * A custom card stack slider for mobile devices
 * Cards are stacked with the active card in front, previous cards partially visible on left,
 * next cards partially visible on right
 */
if (!customElements.get('info-cards-slider')) {
  class InfoCardsSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isDragging = false;
      this.startX = 0;
      this.currentX = 0;
      this.dragThreshold = 50; // Minimum drag distance to trigger slide
      this.isAnimating = false;
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.cards = Array.from(this.querySelectorAll('.info-card'));
      this.totalCards = this.cards.length;
      
      if (this.totalCards <= 1) return;
      
      // Only initialize on mobile
      this.checkMobile();
      this.resizeHandler = this.checkMobile.bind(this);
      window.addEventListener('resize', FoxTheme.utils.debounce(this.resizeHandler, 150));
      
      // Listen for theme editor events
      if (Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect.bind(this));
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.resizeHandler);
      this.destroySlider();
    }

    checkMobile() {
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      
      if (isMobile && !this.sliderInitialized) {
        this.initSlider();
      } else if (!isMobile && this.sliderInitialized) {
        this.destroySlider();
      }
    }

    initSlider() {
      this.sliderInitialized = true;
      this.classList.add('info-cards-slider--active');
      
      // Position all cards
      this.updateCardsPosition();
      
      // Calculate and set container height
      this.updateContainerHeight();
      
      // Add touch/mouse event listeners
      this.bindEvents();
      
      // Create pagination dots
      this.createPagination();
      
      // Update height when images load
      this.setupImageLoadListeners();
    }

    destroySlider() {
      this.sliderInitialized = false;
      this.classList.remove('info-cards-slider--active');
      
      // Remove inline styles from cards and container
      this.cards.forEach(card => {
        card.style.cssText = '';
        card.classList.remove('info-card--active', 'info-card--prev', 'info-card--next', 'info-card--hidden');
      });
      
      // Reset container height
      const container = this.querySelector('.info-cards__items');
      if (container) {
        container.style.height = '';
        container.style.minHeight = '';
      }
      
      // Remove pagination
      const pagination = this.querySelector('.info-cards-pagination');
      if (pagination) pagination.remove();
      
      // Remove event listeners
      this.unbindEvents();
    }

    updateContainerHeight() {
      const container = this.querySelector('.info-cards__items');
      if (!container) return;

      // Measure only the active card's natural content height.
      // scrollHeight is unaffected by position, transform or opacity,
      // so no temporary style manipulation is needed.
      const activeCard = this.cards[this.currentIndex];
      if (!activeCard) return;

      const activeHeight = activeCard.scrollHeight;

      if (activeHeight > 0) {
        container.style.height = activeHeight + 'px';
        container.style.maxHeight = activeHeight + 'px';
      }
    }

    setupImageLoadListeners() {
      const images = this.querySelectorAll('img');
      
      images.forEach(img => {
        if (img.complete) {
          // Image already loaded
          return;
        }
        
        img.addEventListener('load', () => {
          // Recalculate container height when image loads
          this.updateContainerHeight();
        }, { once: true });
      });
    }

    bindEvents() {
      // Touch events
      this.touchStartHandler = this.handleTouchStart.bind(this);
      this.touchMoveHandler = this.handleTouchMove.bind(this);
      this.touchEndHandler = this.handleTouchEnd.bind(this);
      
      this.addEventListener('touchstart', this.touchStartHandler, { passive: true });
      this.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
      this.addEventListener('touchend', this.touchEndHandler, { passive: true });
      
      // Mouse events for desktop testing
      this.mouseDownHandler = this.handleMouseDown.bind(this);
      this.mouseMoveHandler = this.handleMouseMove.bind(this);
      this.mouseUpHandler = this.handleMouseUp.bind(this);
      this.mouseLeaveHandler = this.handleMouseUp.bind(this);
      
      this.addEventListener('mousedown', this.mouseDownHandler);
      document.addEventListener('mousemove', this.mouseMoveHandler);
      document.addEventListener('mouseup', this.mouseUpHandler);
      this.addEventListener('mouseleave', this.mouseLeaveHandler);
    }

    unbindEvents() {
      this.removeEventListener('touchstart', this.touchStartHandler);
      this.removeEventListener('touchmove', this.touchMoveHandler);
      this.removeEventListener('touchend', this.touchEndHandler);
      
      this.removeEventListener('mousedown', this.mouseDownHandler);
      document.removeEventListener('mousemove', this.mouseMoveHandler);
      document.removeEventListener('mouseup', this.mouseUpHandler);
      this.removeEventListener('mouseleave', this.mouseLeaveHandler);
    }

    handleTouchStart(e) {
      if (this.isAnimating) return;
      this.isDragging = true;
      this.startX = e.touches[0].clientX;
      this.currentX = this.startX;
    }

    handleTouchMove(e) {
      if (!this.isDragging || this.isAnimating) return;
      
      this.currentX = e.touches[0].clientX;
      const diff = this.currentX - this.startX;
      
      // Prevent default only when swiping horizontally
      if (Math.abs(diff) > 10) {
        e.preventDefault();
      }
      
      this.updateDragPosition(diff);
    }

    handleTouchEnd(e) {
      if (!this.isDragging || this.isAnimating) return;
      this.isDragging = false;
      
      const diff = this.currentX - this.startX;
      this.handleDragEnd(diff);
    }

    handleMouseDown(e) {
      if (this.isAnimating) return;
      this.isDragging = true;
      this.startX = e.clientX;
      this.currentX = this.startX;
      e.preventDefault();
    }

    handleMouseMove(e) {
      if (!this.isDragging || this.isAnimating) return;
      
      this.currentX = e.clientX;
      const diff = this.currentX - this.startX;
      this.updateDragPosition(diff);
    }

    handleMouseUp(e) {
      if (!this.isDragging || this.isAnimating) return;
      this.isDragging = false;
      
      const diff = this.currentX - this.startX;
      this.handleDragEnd(diff);
    }

    updateDragPosition(diff) {
      const activeCard = this.cards[this.currentIndex];
      const maxDrag = 100;
      const clampedDiff = Math.max(-maxDrag, Math.min(maxDrag, diff * 0.5));
      
      // Add visual feedback during drag
      activeCard.style.transform = `translateX(${clampedDiff}px) scale(1)`;
      
      // Update next/prev cards opacity based on drag direction
      if (diff < 0 && this.currentIndex < this.totalCards - 1) {
        // Dragging left - show next card coming forward
        const nextCard = this.cards[this.currentIndex + 1];
        const progress = Math.min(1, Math.abs(diff) / this.dragThreshold);
        nextCard.style.transform = `translateX(${40 - (progress * 20)}%) scale(${0.9 + (progress * 0.05)})`;
        nextCard.style.opacity = 0.7 + (progress * 0.3);
      } else if (diff > 0 && this.currentIndex > 0) {
        // Dragging right - show prev card coming forward
        const prevCard = this.cards[this.currentIndex - 1];
        const progress = Math.min(1, Math.abs(diff) / this.dragThreshold);
        prevCard.style.transform = `translateX(${-40 + (progress * 20)}%) scale(${0.9 + (progress * 0.05)})`;
        prevCard.style.opacity = 0.7 + (progress * 0.3);
      }
    }

    handleDragEnd(diff) {
      if (Math.abs(diff) > this.dragThreshold) {
        if (diff < 0 && this.currentIndex < this.totalCards - 1) {
          // Swipe left - go to next card
          this.goToCard(this.currentIndex + 1);
        } else if (diff > 0 && this.currentIndex > 0) {
          // Swipe right - go to previous card
          this.goToCard(this.currentIndex - 1);
        } else {
          // Can't go further, reset position
          this.updateCardsPosition();
        }
      } else {
        // Didn't drag enough, reset position
        this.updateCardsPosition();
      }
    }

    goToCard(index) {
      if (index < 0 || index >= this.totalCards || this.isAnimating) return;
      
      this.isAnimating = true;
      this.currentIndex = index;
      
      this.updateCardsPosition(true);
      this.updateContainerHeight();
      this.updatePagination();
      
      // Reset animation flag after transition
      setTimeout(() => {
        this.isAnimating = false;
      }, 400);
    }

    updateCardsPosition(animate = false) {
      this.cards.forEach((card, index) => {
        // Remove all state classes
        card.classList.remove('info-card--active', 'info-card--prev', 'info-card--next', 'info-card--hidden');
        
        // Add transition class if animating
        if (animate) {
          card.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1), opacity 0.4s ease';
        } else {
          card.style.transition = 'none';
        }
        
        const diff = index - this.currentIndex;
        
        if (diff === 0) {
          // Active card - front and center
          card.classList.add('info-card--active');
          card.style.transform = 'translateX(0) scale(1)';
          card.style.opacity = '1';
          card.style.zIndex = this.totalCards;
          card.style.pointerEvents = 'auto';
        } else if (diff === -1) {
          // Previous card - behind on left
          card.classList.add('info-card--prev');
          card.style.transform = 'translateX(-40%) scale(0.9)';
          card.style.opacity = '0.6';
          card.style.zIndex = this.totalCards - 1;
          card.style.pointerEvents = 'none';
        } else if (diff === 1) {
          // Next card - behind on right
          card.classList.add('info-card--next');
          card.style.transform = 'translateX(40%) scale(0.9)';
          card.style.opacity = '0.6';
          card.style.zIndex = this.totalCards - 1;
          card.style.pointerEvents = 'none';
        } else if (diff < -1) {
          // Cards further back on left - hidden
          card.classList.add('info-card--hidden');
          card.style.transform = 'translateX(-80%) scale(0.8)';
          card.style.opacity = '0';
          card.style.zIndex = this.totalCards - Math.abs(diff);
          card.style.pointerEvents = 'none';
        } else {
          // Cards further back on right - hidden
          card.classList.add('info-card--hidden');
          card.style.transform = 'translateX(80%) scale(0.8)';
          card.style.opacity = '0';
          card.style.zIndex = this.totalCards - Math.abs(diff);
          card.style.pointerEvents = 'none';
        }
      });
    }

    createPagination() {
      // Remove existing pagination if any
      const existingPagination = this.querySelector('.info-cards-pagination');
      if (existingPagination) existingPagination.remove();
      
      const pagination = document.createElement('div');
      pagination.className = 'info-cards-pagination';
      
      for (let i = 0; i < this.totalCards; i++) {
        const dot = document.createElement('button');
        dot.className = 'info-cards-pagination__dot';
        dot.setAttribute('aria-label', `Go to card ${i + 1}`);
        if (i === this.currentIndex) {
          dot.classList.add('info-cards-pagination__dot--active');
        }
        dot.addEventListener('click', () => this.goToCard(i));
        pagination.appendChild(dot);
      }
      
      this.appendChild(pagination);
    }

    updatePagination() {
      const dots = this.querySelectorAll('.info-cards-pagination__dot');
      dots.forEach((dot, index) => {
        dot.classList.toggle('info-cards-pagination__dot--active', index === this.currentIndex);
      });
    }

    handleBlockSelect(e) {
      if (e.detail.sectionId !== this.sectionId) return;
      
      const selectedBlock = e.target;
      const index = parseInt(selectedBlock.dataset.index, 10);
      
      if (!isNaN(index) && this.sliderInitialized) {
        this.goToCard(index);
      }
    }
  }

  customElements.define('info-cards-slider', InfoCardsSlider);
}
